import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { buildDailyCloseReport, openWhatsAppShare, formatReportDateBR, formatKg, PAYMENT_METHOD_KEYS } from '@/lib/dailyClose';
import { archivePaidCommands } from '@/lib/commands';
import { formatCurrency } from '@/lib/formatters';
import { printHTML, dailyCloseHTML } from '@/lib/printerWindows';
import { useAuth } from '@/context/AuthContext';
import { Share2, Printer, Banknote, QrCode, CreditCard, Ticket, Calendar, Sparkles, ArrowLeft, LogOut, Scale, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const METHOD_ICON = {
  Dinheiro: Banknote,
  Pix: QrCode,
  'Crédito': CreditCard,
  'Débito': CreditCard,
  Voucher: Ticket,
};

// YYYY-MM-DD local timezone (sem shift UTC)
function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function ymdToDate(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0); // meio-dia local evita DST edge cases
}

export default function DailyCloseModal({ open, onOpenChange }) {
  // v7.70 — bug latente corrigido: handleExit chamava navigate('/') sem o
  // hook instanciado (useNavigate importado mas nunca usado) → ReferenceError.
  const navigate = useNavigate();
  const { operator } = useAuth();
  const [dateYmd, setDateYmd] = useState(todayYmd());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Reset para hoje sempre que abre
    setDateYmd(todayYmd());
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    const refDate = ymdToDate(dateYmd);
    buildDailyCloseReport(refDate)
      .then((r) => { if (alive) setReport(r); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, dateYmd]);

  const handleShare = () => {
    if (!report) return;
    openWhatsAppShare(report);
  };

  const handlePrint = async () => {
    if (!report) return;
    try {
      const operatorName = operator?.name || 'Operador';
      await printHTML(dailyCloseHTML(report, operatorName), 'Fechamento Flash');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[FECHAMENTO FLASH] impressão falhou:', err);
      toast.error('Falha ao imprimir cupom', {
        description: String(err?.message || err),
      });
    }
  };

  const handleBack = () => {
    onOpenChange(false);
  };

  const handleExit = () => {
    onOpenChange(false);
    navigate('/');
  };

  // Fase 25 — Arquiva comandas pagas do dia e libera a numeração (1..99) pro próximo dia.
  const handleResetNumbering = async () => {
    const confirm = window.confirm(
      'Zerar numeração de comandas?\n\n' +
      'Esta ação arquiva todas as comandas pagas (histórico preservado em relatórios) e libera os números 1-99 para o próximo dia.\n\n' +
      'Comandas abertas NÃO serão afetadas.'
    );
    if (!confirm) return;
    try {
      const { archived } = await archivePaidCommands();
      if (archived === 0) {
        toast.info('Nenhuma comanda paga para arquivar', {
          description: 'A numeração já está liberada.',
        });
      } else {
        toast.success(`${archived} comanda${archived === 1 ? '' : 's'} arquivada${archived === 1 ? '' : 's'}`, {
          description: 'Numeração zerada. Próxima comanda começa em #1.',
        });
      }
    } catch (err) {
      toast.error(err?.message || 'Falha ao arquivar comandas');
    }
  };

  const dateStr = report ? formatReportDateBR(report.date) : '';
  const isToday = dateYmd === todayYmd();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="daily-close-modal"
        className="max-w-lg bg-white border border-slate-200 rounded-2xl p-0 overflow-hidden max-h-[92vh] flex flex-col"
      >
        <div id="daily-close-printable" ref={printRef} className="bg-white flex-1 overflow-y-auto">
          {/* Header com logo transparente */}
          <DialogHeader className="px-6 pt-6 pb-2">
            <div className="flex flex-col items-center mb-3">
              <img
                data-testid="daily-close-logo"
                src="/logo-transparente.webp"
                alt="Kaven Restaurante"
                className="block object-contain w-full"
                style={{ maxWidth: '180px' }}
              />
            </div>
            <DialogTitle className="text-center font-heading text-xl font-bold text-slate-900">
              Fechamento Flash
            </DialogTitle>
            <DialogDescription className="sr-only">
              Resumo financeiro do dia com faturamento por método de pagamento, gorjetas, receita de café e top itens.
            </DialogDescription>

            {/* Seletor de data - permite navegar para dias anteriores */}
            <div className="mt-3 flex items-center justify-center gap-2 print:hidden">
              <div className="relative">
                <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  data-testid="daily-close-date-picker"
                  type="date"
                  value={dateYmd}
                  max={todayYmd()}
                  onChange={(e) => setDateYmd(e.target.value)}
                  className="h-9 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 focus:outline-none focus:border-slate-400 transition-colors"
                />
              </div>
              {!isToday && (
                <button
                  data-testid="daily-close-today-btn"
                  type="button"
                  onClick={() => setDateYmd(todayYmd())}
                  className="h-9 px-3 rounded-lg bg-slate-900 text-white text-[11px] font-bold hover:bg-slate-800 transition-colors"
                >
                  Hoje
                </button>
              )}
            </div>

            <p data-testid="daily-close-date-label" className="text-center text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mt-2">
              {isToday ? `Hoje · ${dateStr}` : dateStr}
            </p>
          </DialogHeader>

          {loading || !report ? (
            <div className="px-6 py-10 text-center text-slate-500 text-sm">
              Calculando...
            </div>
          ) : (
            <div className="px-6 pb-5 space-y-4">
              {/* Totais principais */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700/70">Faturamento</p>
                  <p
                    data-testid="report-total-revenue"
                    className="font-mono text-2xl font-black text-emerald-700 tracking-tighter mt-1"
                  >
                    {formatCurrency(report.totalRevenue)}
                  </p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Comandas Pagas</p>
                  <p
                    data-testid="report-total-commands"
                    className="font-mono text-2xl font-black text-slate-900 tracking-tighter mt-1"
                  >
                    {report.totalCommands}
                  </p>
                  {(report.regularCommandsCount > 0 || report.coffeeCommandsCount > 0) && (
                    <p data-testid="report-commands-breakdown" className="text-[10px] text-slate-500 mt-1 leading-tight">
                      <span className="font-bold text-slate-700">{report.regularCommandsCount}</span> buffet/bebida
                      {report.coffeeCommandsCount > 0 && (
                        <>
                          <span className="text-slate-300 mx-1">·</span>
                          <span className="font-bold text-amber-700">{report.coffeeCommandsCount}</span> café ☕
                        </>
                      )}
                    </p>
                  )}
                </div>
              </div>

              {/* Fase 24 — Peso vendido (do modo balança) — sempre visível */}
              <div
                data-testid="report-kg-card"
                className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Scale size={16} strokeWidth={1.75} className="text-blue-700" />
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-blue-700/80">
                      Peso Vendido (Buffet)
                    </p>
                    <p className="text-[10px] text-blue-700/60 mt-0.5">
                      Somatório das pesagens do dia
                    </p>
                  </div>
                </div>
                <p
                  data-testid="report-kg-total"
                  className="font-mono text-lg font-black text-blue-700 tracking-tighter"
                >
                  {formatKg(report.totalKgSold || 0)}
                </p>
              </div>

              {/* Breakdown por método */}
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">
                  Por Método de Pagamento
                </p>
                <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
                  {PAYMENT_METHOD_KEYS.map((k) => {
                    const Icon = METHOD_ICON[k];
                    const m = report.byMethod[k];
                    return (
                      <div
                        key={k}
                        data-testid={`report-method-${k.toLowerCase()}`}
                        className="flex items-center justify-between px-3 py-2.5"
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon size={16} strokeWidth={1.75} className="text-slate-500" />
                          <span className="text-sm font-semibold text-slate-700">{k}</span>
                          <span className="text-[10px] font-bold text-slate-400">({m.count})</span>
                        </div>
                        <span
                          data-testid={`report-method-${k.toLowerCase()}-total`}
                          className="font-mono text-sm font-bold text-slate-900"
                        >
                          {formatCurrency(m.total)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Gorjetas (10% serviço) */}
              {report.totalService > 0 && (
                <div
                  data-testid="report-service-card"
                  className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-3 flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles size={16} strokeWidth={1.75} className="text-emerald-700" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700/80">
                        Total de Gorjetas
                      </p>
                      <p className="text-[10px] text-emerald-700/60 mt-0.5">
                        10% de serviço
                      </p>
                    </div>
                  </div>
                  <p
                    data-testid="report-service-total"
                    className="font-mono text-lg font-black text-emerald-700 tracking-tighter"
                  >
                    {formatCurrency(report.totalService)}
                  </p>
                </div>
              )}

              {/* Café da manhã */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700/80">
                    ☕ Café da Manhã
                  </p>
                  <p className="text-[10px] text-amber-700/60 mt-0.5">
                    {report.coffee.count} unidade{report.coffee.count === 1 ? '' : 's'}
                  </p>
                </div>
                <p
                  data-testid="report-coffee-total"
                  className="font-mono text-lg font-black text-amber-700 tracking-tighter"
                >
                  {formatCurrency(report.coffee.total)}
                </p>
              </div>

              {/* Top 3 */}
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">
                  Top 3 Itens
                </p>
                <div data-testid="report-top-items" className="space-y-1.5">
                  {report.topItems.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-2">Sem vendas no período</p>
                  ) : (
                    report.topItems.map((it, i) => (
                      <div
                        key={it.name}
                        className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2"
                      >
                        <div className="w-6 h-6 rounded-full bg-slate-900 text-white text-[10px] font-black flex items-center justify-center">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{it.name}</p>
                          <p className="text-[10px] text-slate-400">{it.count}x</p>
                        </div>
                        <p className="font-mono text-sm font-bold text-slate-700">
                          {formatCurrency(it.total)}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <p className="text-center text-[10px] text-slate-400 pt-1">
                Gerado pelo Sistema Kaven
              </p>
            </div>
          )}
        </div>

        {/* Actions - escondidos na impressão */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 print:hidden shrink-0">
          <div className="flex gap-2 mb-2">
            <button
              data-testid="daily-close-share-btn"
              onClick={handleShare}
              disabled={loading || !report}
              className="flex-1 flex items-center justify-center gap-2 h-11 rounded-lg bg-[#25D366] text-white text-sm font-bold hover:bg-[#1ebe57] disabled:opacity-40 transition-colors"
            >
              <Share2 size={16} strokeWidth={2} />
              Compartilhar
            </button>
            <button
              data-testid="daily-close-print-btn"
              onClick={handlePrint}
              disabled={loading || !report}
              className="flex-1 flex items-center justify-center gap-2 h-11 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-40 transition-colors"
            >
              <Printer size={16} strokeWidth={2} />
              Imprimir
            </button>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="daily-close-back-btn"
              onClick={handleBack}
              className="flex-1 flex items-center justify-center gap-2 h-10 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-bold hover:bg-slate-100 transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={2} />
              Voltar
            </button>
            <button
              data-testid="daily-close-exit-btn"
              onClick={handleExit}
              className="flex-1 flex items-center justify-center gap-2 h-10 rounded-lg border border-slate-300 bg-white text-slate-600 text-sm font-bold hover:bg-slate-100 transition-colors"
            >
              <LogOut size={14} strokeWidth={2} />
              Sair
            </button>
          </div>
          <button
            data-testid="daily-close-reset-btn"
            onClick={handleResetNumbering}
            disabled={loading}
            className="mt-2 w-full flex items-center justify-center gap-2 h-10 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 text-amber-800 text-xs font-bold hover:bg-amber-100 hover:border-amber-400 disabled:opacity-40 transition-colors"
            title="Libera os números 1-99 para o próximo dia (histórico é preservado)"
          >
            <RotateCcw size={13} strokeWidth={2.25} />
            Zerar Numeração de Comandas
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
